import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { aktuelleBildBreite } from "./bildBearbeiten";
import "./notizbloecke.css";

/**
 * Rahmen, Ziehgriff und Leiste eines ausgewählten Bilds im Notizfeld
 * (User 23.10.2026: „Bild in der Notiz vergrößern/verkleinern … entfernen").
 *
 * ══ WAS EIN KLICK TUT ══════════════════════════════════════════════════════
 * Im bearbeitbaren Feld WÄHLT ein Klick das Bild aus — dann steht dieser
 * Rahmen darum, unten rechts der Griff (Ziehen ändert die Breite, das
 * Seitenverhältnis bleibt), oben die Leiste mit −, +, Lupe und ×. Groß
 * öffnet der Doppelklick oder die Lupe. Bis zum 23.10. öffnete schon der
 * einfache Klick — dann gäbe es keinen Weg, das Bild anzufassen, ohne dass
 * der Viewer aufgeht. Im schreibgeschützten Feld öffnet weiter der Klick.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ NICHTS DAVON STEHT IM TEXT ═════════════════════════════════════════════
 * Rahmen und Griff sind ein Portal am `body` (`position: fixed`), keine Klasse
 * am Bild: Eine Klasse im contentEditable stünde im `innerHTML`, und dann
 * passte der Rückgängig-Verlauf (`formatVerlauf.ts` vergleicht genau das)
 * nach dem Abwählen nicht mehr. Alle Knöpfe verhindern ihr `mousedown`, der
 * Fokus bleibt im Feld — Entf und Strg+Z wirken weiter.
 *
 * Die Lage wird je Bildschirmbild nachgemessen, solange das Bild gewählt
 * ist: Scrollen, ein nachladendes Bild darüber, eine neue Zeile — alles
 * verschiebt das Bild, ohne dass jemand Bescheid sagt.
 * ═══════════════════════════════════════════════════════════════════════════
 */

// jsdom kennt `requestAnimationFrame` nur mit `pretendToBeVisual`.
const naechstesBild = (fn: () => void): number =>
    typeof window.requestAnimationFrame === "function"
        ? window.requestAnimationFrame(fn)
        : (window.setTimeout(fn, 16) as unknown as number);
const bildAbbrechen = (id: number): void =>
    typeof window.cancelAnimationFrame === "function" ? window.cancelAnimationFrame(id) : window.clearTimeout(id);

type Rahmen = { left: number; top: number; width: number; height: number };

const gleich = (a: Rahmen | null, b: Rahmen | null) =>
    !!a && !!b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;

export type BildLeisteProps = {
    img: HTMLImageElement;
    onKleiner: () => void;
    onGroesser: () => void;
    onEntfernen: () => void;
    onOeffnen?: () => void;
    /** Ziehen am Griff beginnt — der Aufrufer merkt sich den Zustand davor. */
    onZiehBeginn: () => void;
    /** Neue Wunschbreite in Pixeln (der Aufrufer begrenzt). */
    onZiehen: (breite: number) => void;
    onZiehEnde: () => void;
    /** Das Bild hängt nicht mehr im Feld (Rückgängig, Abgleich). */
    onWeg: () => void;
};

const halt = (e: React.MouseEvent | React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
};

export const BildLeiste: React.FC<BildLeisteProps> = ({
    img,
    onKleiner,
    onGroesser,
    onEntfernen,
    onOeffnen,
    onZiehBeginn,
    onZiehen,
    onZiehEnde,
    onWeg,
}) => {
    const [rahmen, setRahmen] = useState<Rahmen | null>(null);
    const wegRef = useRef(onWeg);
    wegRef.current = onWeg;
    const zugRef = useRef<{ x: number; breite: number; id: number } | null>(null);

    useEffect(() => {
        let lauf = 0;
        const messen = () => {
            if (!img.isConnected) {
                wegRef.current();
                return;
            }
            const r = img.getBoundingClientRect();
            const neu = { left: r.left, top: r.top, width: r.width, height: r.height };
            setRahmen((alt) => (gleich(alt, neu) ? alt : neu));
            lauf = naechstesBild(messen);
        };
        messen();
        return () => bildAbbrechen(lauf);
    }, [img]);

    if (!rahmen) return null;

    const griffRunter = (e: React.PointerEvent<HTMLSpanElement>) => {
        halt(e);
        zugRef.current = { x: e.clientX, breite: aktuelleBildBreite(img), id: e.pointerId };
        try {
            e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
            // jsdom kennt kein Pointer-Capture.
        }
        onZiehBeginn();
    };
    const griffZiehen = (e: React.PointerEvent<HTMLSpanElement>) => {
        const zug = zugRef.current;
        if (!zug || zug.id !== e.pointerId) return;
        e.preventDefault();
        onZiehen(zug.breite + (e.clientX - zug.x));
    };
    const griffLos = (e: React.PointerEvent<HTMLSpanElement>) => {
        const zug = zugRef.current;
        if (!zug || zug.id !== e.pointerId) return;
        zugRef.current = null;
        onZiehEnde();
    };

    // Die Leiste steht ÜBER dem Bild; ist oben kein Platz, darunter.
    const leisteOben = rahmen.top > 44;
    const leisteTop = leisteOben ? rahmen.top - 38 : rahmen.top + rahmen.height + 6;

    return createPortal(
        <div className="kvp-rd nb-bild-auswahl" data-popover-teil="">
            <div
                className="nb-bild-rahmen"
                style={{ left: rahmen.left - 2, top: rahmen.top - 2, width: rahmen.width + 4, height: rahmen.height + 4 }}
                aria-hidden
            />
            <span
                className="nb-bild-griff"
                role="slider"
                aria-label="Bildgröße ziehen"
                aria-valuemin={60}
                aria-valuenow={Math.round(rahmen.width)}
                title="Ziehen, um die Größe zu ändern"
                style={{ left: rahmen.left + rahmen.width - 7, top: rahmen.top + rahmen.height - 7 }}
                onMouseDown={halt}
                onPointerDown={griffRunter}
                onPointerMove={griffZiehen}
                onPointerUp={griffLos}
                onPointerCancel={griffLos}
            />
            <div
                className="nb-markleiste nb-bild-leiste"
                role="toolbar"
                aria-label="Bild"
                style={{ left: Math.max(8, rahmen.left), top: Math.max(8, leisteTop) }}
                onMouseDown={halt}
            >
                <button type="button" className="nb-markleiste-btn nb-bild-btn" title="Kleiner" aria-label="Kleiner" onClick={onKleiner}>
                    −
                </button>
                <button type="button" className="nb-markleiste-btn nb-bild-btn" title="Größer" aria-label="Größer" onClick={onGroesser}>
                    +
                </button>
                {onOeffnen ? (
                    <button
                        type="button"
                        className="nb-markleiste-btn nb-bild-btn"
                        title="Groß ansehen (Doppelklick)"
                        aria-label="Groß ansehen"
                        onClick={onOeffnen}
                    >
                        <svg width={14} height={14} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden>
                            <circle cx="8.5" cy="8.5" r="5.5" />
                            <path d="M12.6 12.6 17 17M8.5 6v5M6 8.5h5" />
                        </svg>
                    </button>
                ) : null}
                <span className="nb-bild-trenner" aria-hidden />
                <button
                    type="button"
                    className="nb-markleiste-btn nb-bild-btn nb-bild-weg"
                    title="Aus der Notiz entfernen (Entf)"
                    aria-label="Bild entfernen"
                    onClick={onEntfernen}
                >
                    ×
                </button>
            </div>
        </div>,
        document.body,
    );
};
